import { NextPageContext, NextPageWithLayout } from 'next';
import React, { ReactElement } from 'react';
import StaticPageComponent from '@fuww/library/src/StaticPage';
import { useIntl } from 'react-intl';
import markdown from '../data/staticPages/international-network.md';
import Layout from '../components/Layout';
import LinkAndAnchor from '../components/LinkAndAnchor';
import messages from '../lib/messages';
import HomeLink from '../components/HomeLink';
import Head from '../components/Head';
import Hero from '../components/Hero';
import redirectToDomain from '../lib/redirectToDomain';
import { getTargetOrigin } from '../lib/redirectRouteToDomain';
import getWindowUrl from '../lib/getWindowUrl';
import getUrlFromRequest from '../lib/getUrlFromRequest';
import getOriginWithPrefix from '../lib/getOrigin';
import Markdown from '../components/Markdown';

const headerImageUrls = {
  mobile: [
    'https://fashionunited.com/r/fit=cover,format=auto,gravity=center,height=34,quality=10,width=42/https://media.fashionunited.com/media/hero/2023/Header_image_internationalnetwork.png',
    'https://fashionunited.com/r/fit=cover,format=auto,gravity=center,height=288,quality=70,width=360/https://media.fashionunited.com/media/hero/2023/Header_image_internationalnetwork.png',
    'https://fashionunited.com/r/fit=cover,format=auto,gravity=center,height=576,quality=70,width=720/https://media.fashionunited.com/media/hero/2023/Header_image_internationalnetwork.png',
  ],
  desktop: [
    'https://fashionunited.com/r/fit=cover,format=auto,gravity=center,height=8,quality=10,width=42/https://media.fashionunited.com/media/hero/2023/Header_image_internationalnetwork.png',
    'https://fashionunited.com/r/fit=cover,format=auto,gravity=center,height=277,quality=70,width=1350/https://media.fashionunited.com/media/hero/2023/Header_image_internationalnetwork.png',
    'https://fashionunited.com/r/fit=cover,format=auto,gravity=center,height=526,quality=70,width=2560/https://media.fashionunited.com/media/hero/2023/Header_image_internationalnetwork.png',
    'https://fashionunited.com/r/fit=cover,format=auto,gravity=center,height=1052,quality=70,width=5120/https://media.fashionunited.com/media/hero/2023/Header_image_internationalnetwork.png',
  ],
};

const bottomImageUrls = {
  mobile: [
    'https://fashionunited.com/r/fit=cover,format=auto,gravity=center,height=34,quality=10,width=42/https://media.fashionunited.com/media/International_Network.png',
    'https://fashionunited.com/r/fit=cover,format=auto,gravity=center,height=288,quality=70,width=360/https://media.fashionunited.com/media/International_Network.png',
    'https://fashionunited.com/r/fit=cover,format=auto,gravity=center,height=576,quality=70,width=720/https://media.fashionunited.com/media/International_Network.png',
  ],
  desktop: [
    'https://fashionunited.com/r/fit=cover,format=auto,gravity=center,height=8,quality=10,width=42/https://media.fashionunited.com/media/International_Network.png',
    'https://fashionunited.com/r/fit=cover,format=auto,gravity=center,height=277,quality=70,width=1350/https://media.fashionunited.com/media/International_Network.png',
    'https://fashionunited.com/r/fit=cover,format=auto,gravity=center,height=526,quality=70,width=2560/https://media.fashionunited.com/media/International_Network.png',
    'https://fashionunited.com/r/fit=cover,format=auto,gravity=center,height=1052,quality=70,width=5120/https://media.fashionunited.com/media/International_Network.png',
  ],
};

const InternationalNetworkPage: NextPageWithLayout = () => {
  const intl = useIntl();

  const breadcrumbs = [
    <HomeLink />,
    <LinkAndAnchor
      route="international-network"
      passHref
    >
      {intl.formatMessage(messages['internationalNetwork.title'])}
    </LinkAndAnchor>,
  ];

  return (
    <>
      <Head
        title={intl.formatMessage(messages['internationalNetwork.title'])}
        description={intl.formatMessage(
          messages['internationalNetwork.title'],
        )}
      />
      <StaticPageComponent
        content={<Markdown markdown={markdown} />}
        title={intl.formatMessage(messages['internationalNetwork.title'])}
        breadcrumbs={breadcrumbs}
      />
    </>
  );
};

InternationalNetworkPage.getLayout = (page: ReactElement) => (
  <Layout
    header={(
      <Hero
        imageUrls={headerImageUrls}
        alt="FashionUnited business cases"
      />
    )}
    bottomImage={(
      <Hero
        imageUrls={bottomImageUrls}
        alt="Countries where FashionUnited is active"
      />
    )}
  >
    {page}
  </Layout>
);

InternationalNetworkPage.getInitialProps = async (context: NextPageContext) => {
  const { req, res } = context;
  const url = req ? getUrlFromRequest(req) : getWindowUrl();

  const currentOrigin = getOriginWithPrefix(url);
  const targetOrigin = getTargetOrigin(url, 'fashionunited.info');

  if (currentOrigin !== targetOrigin) {
    redirectToDomain(
      '/international-network',
      res,
      targetOrigin,
    );
  }

  return {};
};

export default InternationalNetworkPage;
